import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api';

const ASSETS = 'https://customer-assets-jai6qajn.emergentagent.net/job_cheratina-b2b/artifacts';
const STOREFRONT = `${ASSETS}/g2q38xvt_entrance%20commercial%20office%20in%20Milan.png`;
const OFFICE_DESK = `${ASSETS}/wmi48mgt_one%20office%20in%20milan%20showroom.png`;
const RECEPTION = `${ASSETS}/jaua37gk_reception%20showroom%20%2B%20commercial%20office.png`;

export default function AboutPage() {
  const { t, i18n } = useTranslation();
  const [about, setAbout] = useState(null);
  const [quoteQr, setQuoteQr] = useState(null);

  useEffect(() => {
    api.get('/about').then(r => setAbout(r.data)).catch(() => {});
    api.get('/quote-qr').then(r => setQuoteQr(r.data)).catch(() => {});
  }, []);

  const lang = i18n.language;
  const content = about?.translations?.[lang] || about?.translations?.en || about?.translations?.it || {};

  return (
    <div>
      {/* HERO — Milan storefront */}
      <section className="relative min-h-[80vh] flex items-end overflow-hidden bg-[#1E1E1E]">
        <div className="absolute inset-0">
          <img src={STOREFRONT} alt="COSMETISSAGE Milan storefront" data-testid="about-hero-image" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1E1E1E] via-[#1E1E1E]/40 to-transparent" />
        </div>
        <div className="relative max-w-[1440px] w-full mx-auto px-6 md:px-12 py-24 text-white fade-up">
          <div className="text-xs uppercase tracking-[0.4em] text-[#C5A059] mb-6" data-testid="about-eyebrow">— Made in Italy · Since 2010</div>
          <h1 className="font-serif font-light text-6xl md:text-8xl leading-none mb-6">
            {content.title || 'COSMETISSAGE'}
          </h1>
          <p className="font-serif italic text-xl md:text-2xl text-white/80 max-w-2xl">
            {content.tagline || 'Italian keratin for the world\'s hair-extension industry.'}
          </p>
        </div>
      </section>

      {/* BRAND STORY */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <div className="text-xs uppercase tracking-[0.3em] text-[#C5A059] mb-4">— Our Story</div>
          <h2 className="font-serif font-light text-4xl md:text-5xl leading-tight">
            {content.mission || 'An Italian brand since 2010.'}
          </h2>
        </div>
        <div className="md:col-span-6 md:col-start-7 space-y-6">
          <p className="text-lg text-[#3D2817]/80 leading-relaxed font-serif whitespace-pre-wrap">
            {content.body || ''}
          </p>
          <div className="pt-6 border-t border-[#E5D9C8]">
            <div className="text-xs uppercase tracking-[0.3em] text-[#3D2817]/60 mb-2">Company</div>
            <div className="font-serif text-2xl text-[#3D2817]">{about?.company_name || 'GLOBAL HAIR & BEAUTY SRL'}</div>
            <div className="text-sm text-[#3D2817]/60 mt-1 uppercase tracking-widest">
              Distributor of COSMETISSAGE<sup className="text-[0.6em]">®</sup>
              {about?.company_vat && <span className="ml-3 font-mono">· VAT {about.company_vat}</span>}
            </div>
          </div>
        </div>
      </section>

      {/* PHOTO SPLIT */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 pb-24 md:pb-32 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
        <figure className="img-zoom aspect-[4/5] overflow-hidden bg-[#F2ECE0]" data-testid="about-photo-office">
          <img src={OFFICE_DESK} alt="Milan office — keratin samples on desk" className="w-full h-full object-cover" />
        </figure>
        <figure className="img-zoom aspect-[4/5] overflow-hidden bg-[#F2ECE0]" data-testid="about-photo-reception">
          <img src={RECEPTION} alt="Milan showroom reception" className="w-full h-full object-cover" />
        </figure>
      </section>

      {/* CONTACT + QR block */}
      <section className="bg-[#3D2817] text-white" data-testid="about-contact-section">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-24 md:py-28">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
            <div className="md:col-span-7">
              <div className="text-xs uppercase tracking-[0.4em] text-[#C5A059] mb-6">— Our Contact</div>
              <h2 className="font-serif font-light text-4xl md:text-6xl leading-none mb-12">
                Get in touch <br />
                <em className="text-[#C5A059] not-italic font-medium">with our Milan team.</em>
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-8 font-serif">
                <div data-testid="contact-company">
                  <div className="text-[10px] uppercase tracking-[0.4em] text-[#C5A059] mb-2">— Company</div>
                  <div className="text-lg">{about?.company_name || 'GLOBAL HAIR & BEAUTY SRL'}</div>
                  <div className="text-xs text-white/50 mt-1 italic">Distributor of COSMETISSAGE<sup className="text-[0.7em]">®</sup></div>
                  {about?.company_vat && <div className="text-sm text-white/60 mt-2 font-mono">VAT {about.company_vat}</div>}
                </div>

                <div data-testid="contact-hq">
                  <div className="text-[10px] uppercase tracking-[0.4em] text-[#C5A059] mb-2">— Registered HQ</div>
                  <address className="not-italic text-lg leading-snug">
                    Via Duchessa Jolanda 17<br />Turin · Italy
                  </address>
                  <div className="text-xs text-white/50 mt-1 italic">Legal seat</div>
                </div>

                <div data-testid="contact-address">
                  <div className="text-[10px] uppercase tracking-[0.4em] text-[#C5A059] mb-2">— Showroom & Offices</div>
                  <address className="not-italic text-lg leading-snug">
                    Viale Abruzzi 14/7<br />
                    20131 Milano · Italy
                  </address>
                  <div className="text-xs text-white/50 mt-1 italic">Commercial operations</div>
                </div>

                <div data-testid="contact-tel">
                  <div className="text-[10px] uppercase tracking-[0.4em] text-[#C5A059] mb-2">— Tel · WhatsApp</div>
                  <a href="tel:+393883886882" className="text-lg block hover:text-[#C5A059] transition-colors">+39 388 388 6882</a>
                  <a href="https://wa.me/393883886882" target="_blank" rel="noreferrer" className="text-sm text-white/60 hover:text-[#C5A059] transition-colors">chat on WhatsApp →</a>
                </div>

                <div data-testid="contact-office-tel">
                  <div className="text-[10px] uppercase tracking-[0.4em] text-[#C5A059] mb-2">— Office</div>
                  <a href="tel:+390236555160" className="text-lg block hover:text-[#C5A059] transition-colors">+39 02 3655 5160</a>
                </div>

                <div data-testid="contact-email">
                  <div className="text-[10px] uppercase tracking-[0.4em] text-[#C5A059] mb-2">— Sales Email</div>
                  <a href={`mailto:${about?.contact_email || 'sales@cosmetissagekeratin.com'}`} className="text-lg block hover:text-[#C5A059] transition-colors break-all">
                    {about?.contact_email || 'sales@cosmetissagekeratin.com'}
                  </a>
                  {about?.contact_email_2 && (
                    <a href={`mailto:${about.contact_email_2}`} className="text-sm text-white/60 hover:text-[#C5A059] transition-colors block mt-1 break-all">
                      {about.contact_email_2}
                    </a>
                  )}
                </div>

                <div data-testid="contact-web">
                  <div className="text-[10px] uppercase tracking-[0.4em] text-[#C5A059] mb-2">— Web</div>
                  <a href={about?.website || 'https://www.cosmetissagekeratin.com'} target="_blank" rel="noreferrer" className="text-lg block hover:text-[#C5A059] transition-colors">
                    www.cosmetissagekeratin.com
                  </a>
                </div>
              </div>
            </div>

            {/* QR column */}
            <div className="md:col-span-5">
              <div className="grid grid-cols-2 gap-6">
                {about?.google_maps_qr && (
                  <a
                    href="https://www.google.com/maps/search/?api=1&query=Viale+Abruzzi+14/7,+20131+Milano"
                    target="_blank"
                    rel="noreferrer"
                    data-testid="about-qr-maps"
                    className="group block"
                  >
                    <div className="bg-white p-3 aspect-square">
                      <img src={about.google_maps_qr} alt="Google Maps QR — COSMETISSAGE Milan" className="w-full h-full object-contain" />
                    </div>
                    <div className="mt-3 text-center text-[10px] uppercase tracking-[0.35em] text-[#C5A059] group-hover:text-white transition-colors">
                      Scan · Google Maps
                    </div>
                  </a>
                )}

                {quoteQr?.data_url && (
                  <a
                    href={quoteQr.url}
                    data-testid="about-qr-quote"
                    className="group block"
                  >
                    <div className="bg-white p-3 aspect-square">
                      <img src={quoteQr.data_url} alt="Request a Quote QR" className="w-full h-full object-contain" />
                    </div>
                    <div className="mt-3 text-center text-[10px] uppercase tracking-[0.35em] text-[#C5A059] group-hover:text-white transition-colors">
                      Scan · Request Quote
                    </div>
                  </a>
                )}
              </div>

              <div className="mt-8 border-t border-[#C5A059]/30 pt-6">
                <div className="text-[10px] uppercase tracking-[0.4em] text-[#C5A059] mb-2">— Business Hours</div>
                <div className="font-serif text-sm text-white/70">Mon–Fri · 9:00 – 18:00 CET</div>
                <div className="font-serif text-sm text-white/70 mt-1">Sat · By appointment</div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
